import { useTranslation } from 'react-i18next';
import type { ChatMessage, ChatReportReason } from '@draughts/shared';
import { CHAT_REPORT_REASONS, CHAT_REPORT_CONFIG, CHAT_REPORT_ERRORS } from '@draughts/shared';
import { reportChat } from '../../services/moderation-api';
import { trackChatReportSubmitted } from '../../utils/analytics';
import ReportModal, { type ReportApiError } from '../common/ReportModal';

interface ChatReportModalProps {
  roomId: string;
  opponentUsername: string;
  /** Last opponent message, attached as context. Null if none is available. */
  reportedMessage: ChatMessage | null;
  onClose: () => void;
  /** Fired once the report is on file (or was already on file): mute the opponent. */
  onReported: () => void;
}

/**
 * Abuse report for the opponent of the current game.
 *
 * The player picks a reason and may add a note; everything else — who is being
 * reported and what was said — is resolved server-side from the live room, so
 * nothing here can be forged.
 *
 * Il vestito e tutta l'accessibilità stanno in `common/ReportModal`, condivisi
 * con la segnalazione dal profilo. Qui resta solo ciò che è di questo flusso: la
 * citazione del messaggio, la chiamata, il muto dell'avversario e i suoi errori.
 */
function ChatReportModal({ roomId, opponentUsername, reportedMessage, onClose, onReported }: ChatReportModalProps) {
  const { t } = useTranslation();

  // Guest preset phrases travel as a stable key: show the localized phrase, not
  // the raw `preset:hello` token a moderator-facing string would leak.
  const quotedText = reportedMessage
    ? reportedMessage.presetKey
      ? t(`chat.presets.${reportedMessage.presetKey}`)
      : reportedMessage.message
    : null;

  return (
    <ReportModal<ChatReportReason>
      title={t('chat.report.title', { username: opponentUsername })}
      intro={t('chat.report.intro')}
      reasons={CHAT_REPORT_REASONS}
      reasonLabel={(r) => t(`chat.report.reasons.${r}`)}
      reasonGroupLabel={t('chat.report.reasonLabel')}
      noteLabel={t('chat.report.noteLabel')}
      notePlaceholder={t('chat.report.notePlaceholder')}
      privacyHint={t('chat.report.privacyHint', { days: CHAT_REPORT_CONFIG.RETENTION_DAYS })}
      submitLabel={t('chat.report.submit')}
      sendingLabel={t('chat.report.sending')}
      doneLabel={t('chat.report.done')}
      quote={
        quotedText
          ? { author: t('chat.report.quoteLabel', { username: opponentUsername }), text: quotedText }
          : null
      }
      onSubmit={async (reason, note) => {
        await reportChat({
          roomId,
          reason,
          ...(reportedMessage ? { messageId: reportedMessage.id } : {}),
          ...(note ? { note } : {}),
        });
        trackChatReportSubmitted(reason);
        onReported();
      }}
      mapError={(err: ReportApiError) => {
        if (err.code === CHAT_REPORT_ERRORS.ALREADY_FILED) {
          // Already on file: the outcome the player wanted is already true, so
          // mute the opponent anyway and just say so.
          onReported();
          return t('chat.report.errors.duplicate');
        }
        if (err.code === CHAT_REPORT_ERRORS.TRANSCRIPT_GONE || err.status === 410) {
          return t('chat.report.errors.gone');
        }
        if (err.code === CHAT_REPORT_ERRORS.NOT_REPORTABLE) {
          return t('chat.report.errors.notReportable');
        }
        if (err.status === 403) return t('chat.report.errors.notInGame');
        if (err.status === 429) return t('chat.report.errors.rateLimited');
        return t('chat.report.errors.generic');
      }}
      onClose={onClose}
    />
  );
}

export default ChatReportModal;
